import { AnalyticsService } from "@core/analytics/analytics-service";
import { CustomDimensions } from "@domain/analytics/custom-dimensions";
import { RoutesPath } from "@ui/navigation/routes";

export enum AnalyticsCategory {
	LOGIN = "LOGIN",
	VIP = "VIP",
	REGISTRATION = "REGISTRATION",
	PATIENT = "PATIENT",
	EXERCISE = "EXERCISE",
	PROGRAM = "PROGRAM",
	ERROR = "ERROR",
	TEMPLATE = "TEMPLATE",
	PERMISSIONS = "PERMISSIONS",
	HEALTHCARE = "HEALTHCARE",
	NOTIFICATIONS = "NOTIFICATIONS",
	PROSPECT = "PROSPECT",
	COMMUNICATION = "COMMUNICATION",
	SETTINGS = "SETTINGS",
	TUTORIAL = "TUTORIAL",
	ANNOUNCEMENT = "ANNOUNCEMENT",
	MODAL = "MODAL",
	SURVEY = "SURVEY",
	GETSTARTED = "GETSTARTED",
	LEARN = "LEARN",
	CONTENT = "CONTENT",
}

export const AnalyticsCategories: Record<AnalyticsCategory, string> = {
	LOGIN: "Login",
	VIP: "Vip",
	REGISTRATION: "Registration",
	PATIENT: "Patient",
	EXERCISE: "Exercise",
	PROGRAM: "Program",
	ERROR: "Error",
	TEMPLATE: "Template",
	PERMISSIONS: "Permissions",
	HEALTHCARE: "Healthcare",
	NOTIFICATIONS: "Notifications",
	PROSPECT: "Prospect",
	COMMUNICATION: "Communication",
	SETTINGS: "Settings",
	ANNOUNCEMENT: "Announcement",
	TUTORIAL: "Tutorial",
	MODAL: "Modal",
	SURVEY: "Survey",
	GETSTARTED: "GetStarted",
	LEARN: "Learn",
	CONTENT: "Content",
};

export enum TrackedRoute {
	Home = "/",
	Login = "/login",
	VipLogin = "/vip/:veevaId",
	ForgotPassword = "/forgot-password",
	PatientDetailsProgress = "/patient-details/:patientId?active=progress",
	PatientDetailsProgram = "/patient-details/:patientId?active=programs",
	ProgramBuilder = "/program-builder/:patientId",
	ProgramList = "/programs/",
	TemplateBuilder = "/template-builder/",
	ExerciseList = "/exercises",
	TermsOfUse = "/legal/terms-of-use",
	PrivacyNotice = "/legal/privacy-notice",
	Settings = "/settings",
	LeavingReasons = "/settings/leaving-reasons",
	AccountDeleted = "/account-deleted",
	Register = "/register",
	RegisterPrivacyNotice = "/register/privacy-notice",
	RegisterTermsOfUse = "/register/terms-of-use",
	Rejected = "/rejected",
	NotificationCenter = "/notifications",
	DemoRequest = "/demo-request",
	InviteYourPt = "/invite-your-physiotherapist",
	ProfessionalInformation = "/professional-information",
	ThankYouSurvey = "/survey-thankyou",
	GetStarted = "/get-started",
	Learn = "/learn",
}

export function getTrackedRouteFromRoutesPath(path: RoutesPath, search?: string): TrackedRoute | null {
	switch (path) {
		case RoutesPath.Home:
			return TrackedRoute.Home;
		case RoutesPath.Login:
			return TrackedRoute.Login;
		case RoutesPath.VipLogin:
			return TrackedRoute.VipLogin;
		case RoutesPath.ForgotPassword:
			return TrackedRoute.ForgotPassword;
		case RoutesPath.PatientDetails:
			return new URLSearchParams(search).get("active") === "progress"
				? TrackedRoute.PatientDetailsProgress
				: TrackedRoute.PatientDetailsProgram;
		case RoutesPath.ProgramBuilder:
			return TrackedRoute.ProgramBuilder;
		case RoutesPath.TemplateBuilder:
			return TrackedRoute.TemplateBuilder;
		case RoutesPath.ProgramList:
			return TrackedRoute.ProgramList;
		case RoutesPath.ExerciseList:
			return TrackedRoute.ExerciseList;
		case RoutesPath.TermsOfUse:
			return TrackedRoute.TermsOfUse;
		case RoutesPath.PrivacyNotice:
			return TrackedRoute.PrivacyNotice;
		case RoutesPath.Settings:
			return TrackedRoute.Settings;
		case RoutesPath.Register:
			return TrackedRoute.Register;
		case RoutesPath.RegisterPrivacyNotice:
			return TrackedRoute.RegisterPrivacyNotice;
		case RoutesPath.RegisterTermsOfUse:
			return TrackedRoute.RegisterTermsOfUse;
		case RoutesPath.NotificationCenter:
			return TrackedRoute.NotificationCenter;
		case RoutesPath.DemoRequest:
			return TrackedRoute.DemoRequest;
		case RoutesPath.InviteYourPt:
			return TrackedRoute.InviteYourPt;
		case RoutesPath.Rejected:
			return TrackedRoute.Rejected;
		case RoutesPath.LeavingReasons:
			return TrackedRoute.LeavingReasons;
		case RoutesPath.AccountDeleted:
			return TrackedRoute.AccountDeleted;
		case RoutesPath.ProfessionalInformation:
			return TrackedRoute.ProfessionalInformation;
		case RoutesPath.ThankYouSurvey:
			return TrackedRoute.ThankYouSurvey;
		case RoutesPath.GetStarted:
			return TrackedRoute.GetStarted;
		case RoutesPath.Learn:
			return TrackedRoute.Learn;
		case RoutesPath.LearnDocument:
			return TrackedRoute.Learn;
		case RoutesPath.VipLoginRedirection:
		case RoutesPath.Debug:
			return null;
	}
}

export const AnalyticsRoutes: Record<TrackedRoute, string> = {
	[TrackedRoute.Home]: "Home",
	[TrackedRoute.Login]: "Login",
	[TrackedRoute.VipLogin]: "Vip Login",
	[TrackedRoute.ForgotPassword]: "ForgotPassword",
	[TrackedRoute.PatientDetailsProgress]: "PatientDetails - Progress",
	[TrackedRoute.PatientDetailsProgram]: "PatientDetails - Program",
	[TrackedRoute.ProgramBuilder]: "ProgramBuilder",
	[TrackedRoute.TermsOfUse]: "TermsOfUse",
	[TrackedRoute.PrivacyNotice]: "PrivacyNotice",
	[TrackedRoute.ProgramList]: "ProgramList",
	[TrackedRoute.TemplateBuilder]: "TemplateBuilder",
	[TrackedRoute.ExerciseList]: "ExerciseList",
	[TrackedRoute.Settings]: "Settings",
	[TrackedRoute.Register]: "Register",
	[TrackedRoute.RegisterPrivacyNotice]: "RegisterPrivacyNotice",
	[TrackedRoute.RegisterTermsOfUse]: "RegisterTermsOfUse",
	[TrackedRoute.NotificationCenter]: "NotificationCenter",
	[TrackedRoute.DemoRequest]: "DemoRequest",
	[TrackedRoute.InviteYourPt]: "InviteYourPt",
	[TrackedRoute.Rejected]: "Rejected",
	[TrackedRoute.LeavingReasons]: "DeleteReasons",
	[TrackedRoute.AccountDeleted]: "AccountDeleted",
	[TrackedRoute.ProfessionalInformation]: "ProfessionalInformation",
	[TrackedRoute.ThankYouSurvey]: "ThankYouSurvey",
	[TrackedRoute.GetStarted]: "GetStarted",
	[TrackedRoute.Learn]: "Learn",
};

export type AppAnalyticsService = AnalyticsService<
	typeof AnalyticsCategories,
	typeof CustomDimensions,
	typeof AnalyticsRoutes
>;
